import axios from 'axios';
import BaseService from './BaseService';
import * as apiroutes from '../apiRoutes';

class S3FileUploadService extends BaseService {
    constructor() {
        super(apiroutes.EVIDANCE_ITEMS)
    }

    /**
     * Get pre-signed URL for S3 upload
     * @param {Object} params - Upload parameters
     * @param {string} params.caseId - Case ID (can be null for temporary uploads)
     * @param {string} params.evidenceId - Evidence ID (can be null)
     * @param {string} params.filename - File name
     * @param {string} params.contentType - MIME type of the file
     * @param {number} params.fileSize - Size of the file in bytes
     * @returns {Promise<Object>} Response with pre-signed URL and upload details
     */
    async getPresignedUrl({ caseId, evidenceId, filename, contentType, fileSize }) {
        try {
            const data = {
                caseId: caseId || null,
                evidenceId: evidenceId || null,
                filename,
                contentType,
                fileSize
            };

            // Use the specific upload-url endpoint
            return await super.makeApiCall('POST', apiroutes.EVIDANCE_ITEMS_UPLOAD, data, null, 'json');
        } catch (error) {
            console.error('Error getting pre-signed URL:', error);
            return {
                success: false,
                error: error.message || 'Failed to get upload URL',
                message: 'Failed to get pre-signed URL from server'
            };
        }
    }

    /**
     * Upload file directly to S3 using pre-signed URL (handles both single and multipart uploads)
     * @param {string|Object} urlData - Pre-signed URL or multipart upload data from backend
     * @param {File} file - File object to upload
     * @param {Function} onProgress - Progress callback function (optional)
     * @returns {Promise<Object>} Upload response
     */
    async uploadToS3(urlData, file, onProgress = null) {
        try {
            // Check if this is multipart upload data
            if (typeof urlData === 'object' && urlData.multipart) {
                return await this.uploadMultipartToS3(urlData, file, onProgress);
            }

            // Handle single file upload (existing logic)
            const presignedUrl = typeof urlData === 'string' ? urlData : urlData.url;

            const config = {
                method: 'PUT',
                url: presignedUrl,
                data: file,
                headers: {
                    'Content-Type': file.type,
                },
                // Track upload progress
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        onProgress(percentCompleted);
                    }
                },
            };

            // Use direct axios for S3 upload (not the instance with interceptors)
            const response = await axios(config);

            return {
                success: true,
                status: response.status,
                message: 'File uploaded successfully to S3',
                uploadData: urlData
            };
        } catch (error) {
            console.error('Error uploading to S3:', error);
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getS3UploadErrorMessage(error)
            };
        }
    }

    /**
     * Upload large file to S3 using multipart upload
     * @param {Object} multipartData - Multipart upload data from backend
     * @param {File} file - File object to upload
     * @param {Function} onProgress - Progress callback function (optional)
     * @returns {Promise<Object>} Upload response
     */
    async uploadMultipartToS3(multipartData, file, onProgress = null) {
        const { key, uploadId, urls, partCount } = multipartData;
        const partSize = 5 * 1024 * 1024; // 5MB
        const parts = [];
        let uploadedBytes = 0;

        try {
            // Upload each part
            for (let i = 0; i < partCount; i++) {
                const start = i * partSize;
                const end = Math.min(start + partSize, file.size);
                const chunk = file.slice(start, end);

                const uploadResponse = await fetch(urls[i], {
                    method: 'PUT',
                    body: chunk,
                    headers: {
                        'Content-Type': file.type,
                    },
                });

                if (!uploadResponse.ok) {
                    throw new Error(`Failed to upload part ${i + 1}: ${uploadResponse.statusText}`);
                }

                const etag = uploadResponse.headers.get('ETag');
                parts.push({
                    PartNumber: i + 1,
                    ETag: etag,
                });

                // Update progress
                uploadedBytes += chunk.size;
                if (onProgress) {
                    const percentCompleted = Math.round((uploadedBytes * 100) / file.size);
                    onProgress(percentCompleted);
                }
            }

            // Complete the multipart upload
            const completeResponse = await this.completeMultipartUpload({
                key,
                uploadId,
                parts
            });

            if (!completeResponse.success) {
                throw new Error('Failed to complete multipart upload');
            }

            return {
                success: true,
                status: 200,
                message: 'Multipart file uploaded successfully to S3',
                uploadData: { ...multipartData, parts }
            };

        } catch (error) {
            console.error('Error in multipart upload:', error);
            return {
                success: false,
                error: error.message,
                message: 'Failed to upload large file using multipart upload'
            };
        }
    }

    /**
     * Complete multipart upload
     * @param {Object} completeData - Completion data
     * @param {string} completeData.key - S3 object key
     * @param {string} completeData.uploadId - Upload ID
     * @param {Array} completeData.parts - Array of part data with PartNumber and ETag
     * @returns {Promise<Object>} Completion response
     */
    async completeMultipartUpload(completeData) {
        try {
            return await super.makeApiCall('POST', apiroutes.EVIDANCE_ITEMS_UPLOAD_COMPLETE, completeData, null, 'json');
        } catch (error) {
            console.error('Error completing multipart upload:', error);
            return {
                success: false,
                error: error.message || 'Failed to complete multipart upload',
                message: 'Failed to complete multipart upload on server'
            };
        }
    }

    /**
     * Complete the upload process by notifying backend
     * This can be used to save file metadata after successful S3 upload
     * @param {Object} uploadData - Upload completion data
     * @param {string} uploadData.key - S3 object key
     * @param {string} uploadData.caseId - Case ID
     * @param {string} uploadData.evidenceId - Evidence ID
     * @param {string} uploadData.filename - Original filename
     * @param {number} uploadData.fileSize - File size
     * @param {string} uploadData.contentType - MIME type
     * @returns {Promise<Object>} Completion response
     */
    async completeUpload(uploadData) {
        try {
            return await super.customCall('POST', '/upload-complete', uploadData, null, 'json');
        } catch (error) {
            console.error('Error completing upload:', error);
            return {
                success: false,
                error: error.message || 'Failed to complete upload',
                message: 'Failed to notify server about upload completion'
            };
        }
    }

    /**
     * Upload multiple files sequentially
     * @param {Array} files - Array of file objects
     * @param {Object} commonParams - Common parameters for all uploads
     * @param {Function} onFileProgress - Progress callback for individual files
     * @param {Function} onOverallProgress - Overall progress callback
     * @returns {Promise<Object>} Results of all uploads
     */
    async uploadMultipleFiles(files, commonParams = {}, onFileProgress = null, onOverallProgress = null) {
        const results = [];
        let completedCount = 0;

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileId = `file_${i}_${Date.now()}`;

                // Get pre-signed URL for this file
                const urlResponse = await this.getPresignedUrl({
                    ...commonParams,
                    filename: file.name,
                    contentType: file.type,
                    fileSize: file.size
                });

                if (!urlResponse.success) {
                    results.push({
                        file,
                        fileId,
                        success: false,
                        error: urlResponse.error || 'Failed to get upload URL'
                    });
                    continue;
                }

                // Upload to S3
                const uploadResponse = await this.uploadToS3(
                    urlResponse.data.multipart ? urlResponse.data : urlResponse.data.url,
                    file,
                    (progress) => {
                        if (onFileProgress) {
                            onFileProgress(fileId, progress);
                        }
                    }
                );

                results.push({
                    file,
                    fileId,
                    success: uploadResponse.success,
                    key: urlResponse.data.key,
                    url: urlResponse.data.multipart ? null : (urlResponse.data.url ? urlResponse.data.url.split('?')[0] : null),
                    error: uploadResponse.error,
                    multipart: urlResponse.data.multipart || false
                });

                completedCount++;

                // Update overall progress
                if (onOverallProgress) {
                    onOverallProgress((completedCount / files.length) * 100);
                }
            }

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;

            return {
                success: failed === 0,
                results,
                summary: {
                    total: files.length,
                    successful,
                    failed
                },
                message: failed === 0
                    ? 'All files uploaded successfully'
                    : `${successful} of ${files.length} files uploaded successfully`
            };

        } catch (error) {
            console.error('Error in multiple file upload:', error);
            return {
                success: false,
                results,
                error: error.message,
                message: 'Multiple file upload process failed'
            };
        }
    }

    /**
     * Get user-friendly error message for S3 upload errors
     * @param {Error} error - Error object
     * @returns {string} User-friendly error message
     */
    getS3UploadErrorMessage(error) {
        const { response } = error;

        if (response?.status === 403) {
            return 'Upload permission denied. The upload URL may have expired.';
        }

        if (response?.status === 400) {
            return 'Invalid file or upload parameters.';
        }

        if (response?.status === 413) {
            return 'File size too large for upload.';
        }

        if (error.code === 'NETWORK_ERROR') {
            return 'Network error during upload. Please check your connection.';
        }

        return 'Failed to upload file to cloud storage. Please try again.';
    }

    /**
     * Validate file before upload
     * @param {File} file - File to validate
     * @param {Object} options - Validation options
     * @returns {Object} Validation result
     */
    validateFile(file, options = {}) {
        const {
            maxSize = 50 * 1024 * 1024, // 50MB default (increased for large files)
            allowedTypes = ['image/*', 'video/*']
        } = options;

        const errors = [];

        // Check file size
        if (file.size > maxSize) {
            errors.push(`File size exceeds ${(maxSize / 1024 / 1024).toFixed(1)}MB limit`);
        }

        // Check file type
        const isValidType = allowedTypes.some(type => {
            if (type.endsWith('/*')) {
                const baseType = type.split('/')[0];
                return file.type.startsWith(baseType);
            }
            return file.type === type;
        });

        if (!isValidType) {
            errors.push(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
        }

        return {
            isValid: errors.length === 0,
            errors,
            file: {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            }
        };
    }

    /**
     * Get file extension from filename
     * @param {string} filename - Filename
     * @returns {string} File extension
     */
    getFileExtension(filename) {
        return filename.split('.').pop().toLowerCase();
    }

    /**
     * Generate unique filename with timestamp
     * @param {string} originalFilename - Original filename
     * @returns {string} Unique filename
     */
    generateUniqueFilename(originalFilename) {
        const timestamp = Date.now();
        const extension = this.getFileExtension(originalFilename);
        const nameWithoutExt = originalFilename.substring(0, originalFilename.lastIndexOf('.'));
        return `${timestamp}_${nameWithoutExt}.${extension}`;
    }

    /**
     * Get pre-signed URL for viewing/downloading a file from S3
     * @param {string} s3Key - S3 key of the file
     * @returns {Promise<Object>} Response with pre-signed download URL
     */
    async getDownloadUrl(s3Key) {
        try {
            const data = { s3Key };
            return await super.makeApiCall('POST', apiroutes.EVIDANCE_ITEMS_DOWNLOAD, data, null, 'json');
        } catch (error) {
            console.error('Error getting download URL:', error);
            return {
                success: false,
                error: error.message || 'Failed to get download URL',
                message: 'Failed to get download URL from server'
            };
        }
    }
}

// Create instance and export it
const S3FileUploadServiceInstance = new S3FileUploadService();
export default S3FileUploadServiceInstance;